/**
 * Detalle de batería (sección 5, la central). Cabecera con estado/perfil/
 * cobertura/versiones/timestamps; tabla de ítems administrados con playback del
 * audio (URL firmada de media), flags y detecciones M1 (códigos PEFF del
 * pre-informe); JSON crudo del PeffReport colapsable (firedRules incluido).
 * Acciones admin: reprocesar y cancelar (con confirmación). Solo LECTURA de los
 * datos clínicos — el admin nunca corrige (eso es del terapeuta vía M11).
 */
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { adminApi, bateriaApi, mediaApi } from "../../api/endpoints.js";
import { useQuery, mensajeDeError } from "../../hooks/useQuery.js";
import type { BateriaCompletaDTO, ItemDTO, PeffReportDTO } from "../../api/types.js";
import { ConfirmDialog, EstadoBadge, PerfilBadge, QaTag, fmtFecha } from "./ui.js";
import s from "./admin.module.css";

interface Deteccion {
  code: string;
  produced: string;
  info: string;
}

/** Índice ref-de-ítem → detecciones M1, extraído del PeffReport (por fonema en la
 *  fonética; por palabra en la fonológica). El motor las produjo; aquí solo se
 *  proyectan junto al ítem que las originó. */
function deteccionesPorRef(rep: PeffReportDTO | null): Map<string, Deteccion[]> {
  const m = new Map<string, Deteccion[]>();
  if (!rep) return m;
  const push = (ref: string, d: Deteccion) => {
    const arr = m.get(ref) ?? [];
    arr.push(d);
    m.set(ref, arr);
  };
  for (const r of rep.phonetic.results) {
    for (const e of r.errors) push(e.item, { code: e.peffProcess, produced: e.produced, info: e.errorType });
  }
  for (const o of rep.phonological.occurrences) {
    if (o.status === "EXCLUIDO_FONETICO") continue;
    push(o.word, { code: o.code, produced: o.produced, info: o.status });
  }
  return m;
}

/** Playback perezoso: pide la URL firmada al pulsar (expira a los 15 min). */
function AudioCell({ audioRef }: { audioRef: string | null }) {
  const [url, setUrl] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!audioRef) return <span className={s.muted}>sin audio</span>;
  if (url) return <audio className={s.audio} src={url} controls autoPlay />;
  return (
    <button
      className={s.btn}
      disabled={cargando}
      onClick={async () => {
        setCargando(true);
        setError(null);
        try {
          const r = await mediaApi.urlAudio(audioRef);
          setUrl(r.url);
        } catch (e) {
          setError(mensajeDeError(e));
        } finally {
          setCargando(false);
        }
      }}
    >
      {cargando ? "…" : error ? "Reintentar" : "▶ Escuchar"}
    </button>
  );
}

function Flags({ item }: { item: ItemDTO }) {
  const flags: [boolean, string, string][] = [
    [item.revisarAudio, "revisar_audio", s.badgeAmber!],
    [item.noEvoca, "no_evoca", s.badgeNeutral!],
    [item.withHint, "with_hint", s.badgeBlue!],
    [item.sinIntento, "sin_intento", s.badgeNeutral!],
    [item.dudoso, "dudoso", s.badgeAmber!],
    [item.pendienteValidacionClinica, "◊ no puntúa", s.badgePurple!],
  ];
  const activos = flags.filter(([on]) => on);
  if (activos.length === 0) return <span className={s.muted}>—</span>;
  return (
    <div className={s.flags}>
      {activos.map(([, txt, clase]) => (
        <span key={txt} className={`${s.badge} ${clase}`}>
          {txt}
        </span>
      ))}
    </div>
  );
}

export function AdminBateriaDetail() {
  const { bateriaId = "" } = useParams();
  const q = useQuery(() => bateriaApi.obtener(bateriaId), [bateriaId]);
  const [accion, setAccion] = useState<"reprocesar" | "cancelar" | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const b: BateriaCompletaDTO | null = q.data;
  const rep = b?.peffReport ?? null;
  const det = useMemo(() => deteccionesPorRef(rep), [rep]);

  const ejercicioCodigo = useMemo(() => {
    const m = new Map<string, { codigo: string; orden: number }>();
    for (const e of b?.ejercicios ?? []) m.set(e.id, { codigo: e.codigo, orden: e.orden });
    return m;
  }, [b]);

  const items = useMemo(() => {
    const arr = [...(b?.items ?? [])];
    arr.sort((x, y) => {
      const ox = ejercicioCodigo.get(x.ejercicioId)?.orden ?? 0;
      const oy = ejercicioCodigo.get(y.ejercicioId)?.orden ?? 0;
      return ox - oy || x.orden - y.orden;
    });
    return arr;
  }, [b, ejercicioCodigo]);

  async function ejecutar() {
    if (!accion || !b) return;
    setOcupado(true);
    setError(null);
    try {
      if (accion === "reprocesar") await adminApi.reprocesar(b.id);
      else await bateriaApi.cancelar(b.id);
      setAccion(null);
      q.reload();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <>
      <Link to="/admin/evaluaciones" className={s.backLink}>
        ‹ Volver a Evaluaciones
      </Link>

      {q.loading && !b ? (
        <div className={s.empty}>Cargando…</div>
      ) : q.error ? (
        <div className={`${s.banner} ${s.bannerError}`}>{q.error}</div>
      ) : !b ? (
        <div className={s.empty}>Batería no encontrada.</div>
      ) : (
        <>
          {b.modoPrueba ? (
            <div className={`${s.banner} ${s.bannerInfo}`}>
              <b>Batería de PRUEBA (QA)</b> — el perfil es real pero el pre-informe NO tiene validez
              clínica.
            </div>
          ) : null}
          {error ? <div className={`${s.banner} ${s.bannerError}`}>{error}</div> : null}

          {/* ---- cabecera ---- */}
          <div className={`${s.panel} ${s.panelPad}`}>
            <div className={s.dl}>
              <div>
                <div className={s.dt}>Estado</div>
                <div className={s.dd}>
                  <EstadoBadge estado={b.estado} /> {b.modoPrueba ? <QaTag /> : null}
                </div>
              </div>
              <div>
                <div className={s.dt}>Perfil</div>
                <div className={s.dd}>
                  <PerfilBadge profile={rep?.profile ?? null} />
                </div>
              </div>
              <div>
                <div className={s.dt}>Banda</div>
                <div className={s.dd}>{b.banda}</div>
              </div>
              <div>
                <div className={s.dt}>Cobertura (§6.4)</div>
                <div className={s.dd}>
                  {rep ? `${Math.round(rep.coverage * 100)}%` : "—"}
                </div>
              </div>
              <div>
                <div className={s.dt}>Inconcluyentes</div>
                <div className={s.dd}>{rep ? rep.inconclusiveCount : "—"}</div>
              </div>
              <div>
                <div className={s.dt}>Compuerta §6.4 omitida</div>
                <div className={s.dd}>{rep ? (rep.coverageGateSkipped ? "sí (QA)" : "no") : "—"}</div>
              </div>
              <div>
                <div className={s.dt}>Engine / Scoring</div>
                <div className={`${s.dd} ${s.mono}`}>
                  {b.engineVersion ?? "—"} / {b.scoringVersion ?? "—"}
                </div>
              </div>
              <div>
                <div className={s.dt}>Inventario</div>
                <div className={`${s.dd} ${s.mono}`}>{b.inventoryVersion ?? "—"}</div>
              </div>
              <div>
                <div className={s.dt}>Niño</div>
                <div className={`${s.dd} ${s.mono}`}>{b.childId}</div>
              </div>
              <div>
                <div className={s.dt}>Creada</div>
                <div className={s.dd}>{fmtFecha(b.createdAt)}</div>
              </div>
              <div>
                <div className={s.dt}>Mic-check</div>
                <div className={s.dd}>{fmtFecha(b.micCheckAt)}</div>
              </div>
              <div>
                <div className={s.dt}>Procesada</div>
                <div className={s.dd}>{fmtFecha(b.procesadoEn)}</div>
              </div>
            </div>

            <div className={s.btnRow} style={{ marginTop: 16 }}>
              <button className={s.btn} onClick={() => setAccion("reprocesar")}>
                Reprocesar batería
              </button>
              <button className={`${s.btn} ${s.btnDanger}`} onClick={() => setAccion("cancelar")}>
                Cancelar batería
              </button>
            </div>
          </div>

          {/* ---- ítems administrados ---- */}
          <div className={s.sectionTitle}>Ítems administrados ({items.length})</div>
          <div className={s.panel}>
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Ejercicio</th>
                    <th>Estímulo</th>
                    <th>Fonema obj.</th>
                    <th>Transcripción ASR</th>
                    <th>Producido</th>
                    <th>Audio</th>
                    <th>Flags</th>
                    <th>Detecciones M1</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const dets = det.get(it.ref) ?? [];
                    const producido = dets.map((d) => d.produced).filter(Boolean);
                    return (
                      <tr key={it.id}>
                        <td>
                          <span className={`${s.badge} ${s.badgeBlue}`}>
                            {ejercicioCodigo.get(it.ejercicioId)?.codigo ?? "?"}
                          </span>
                        </td>
                        <td>{it.ref}</td>
                        <td className={s.mono}>{it.phoneme}</td>
                        <td className={s.muted} title="Scribe v2 es Zero-Retention: no se guarda el texto">
                          no retenida
                        </td>
                        <td className={s.mono}>
                          {producido.length > 0 ? producido.join(" · ") : <span className={s.muted}>—</span>}
                        </td>
                        <td>
                          <AudioCell audioRef={it.audioRef} />
                        </td>
                        <td>
                          <Flags item={it} />
                        </td>
                        <td>
                          {dets.length === 0 ? (
                            <span className={s.muted}>—</span>
                          ) : (
                            <div className={s.detections}>
                              {dets.map((d, i) => (
                                <span
                                  key={`${d.code}-${i}`}
                                  className={`${s.badge} ${s.badgeRed}`}
                                  title={`${d.info}${d.produced ? ` · producido: ${d.produced}` : ""}`}
                                >
                                  {d.code}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={8} className={s.empty}>
                        Sin ítems administrados.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>

          {/* ---- JSON crudo del PeffReport ---- */}
          <div className={s.sectionTitle}>Pre-informe PEFF (JSON)</div>
          <div className={`${s.panel} ${s.panelPad}`}>
            {rep ? (
              <details className={s.details}>
                <summary>Ver PeffReport crudo (firedRules incluido)</summary>
                <pre className={s.jsonBox}>{JSON.stringify(rep, null, 2)}</pre>
              </details>
            ) : (
              <span className={s.muted}>La batería aún no fue procesada: no hay pre-informe.</span>
            )}
          </div>
        </>
      )}

      {accion ? (
        <ConfirmDialog
          titulo={accion === "reprocesar" ? "Reprocesar batería" : "Cancelar batería"}
          cuerpo={
            accion === "reprocesar" ? (
              "Se re-ejecutará el scoring (ASR + motor) sobre los ítems ya administrados. No toca baterías con informe clínico firmado."
            ) : (
              "La batería quedará cancelada. Esta acción no se puede deshacer."
            )
          }
          confirmar={accion === "reprocesar" ? "Reprocesar" : "Cancelar batería"}
          peligro={accion === "cancelar"}
          ocupado={ocupado}
          onConfirm={() => void ejecutar()}
          onCancel={() => setAccion(null)}
        />
      ) : null}
    </>
  );
}
